import React, { useState, useEffect } from 'react';
import Rating from './Rating';
import StarRating from './StarRating';
import ServerStatus from './ServerStatus';
import ServerTags from './ServerTags';
import { useLanguage } from '../contexts/LanguageContext';
import { serverAPI } from '../utils/api';

interface SocialMediaLink {
  id: number;
  name: string;
  url: string;
  icon: React.ReactNode;
}

interface ServerInfoPanelProps {
  title: string;
  description: string;
  rating: number;
  status: 'online' | 'offline' | 'maintenance' | 'starting';
  launchDate: string;
  playersOnline?: number;
  maxPlayers?: number;
  version?: string;
  tags?: string[];
  className?: string;
  socialMedia?: SocialMediaLink[];
  homepageUrl?: string;
  downloadLink?: string;
  discordLink?: string;
  banner?: string;
  onDownload?: () => void;
  isFavorite?: boolean;
  serverId?: string;
  canEdit?: boolean;
  onEdit?: () => void;
}

const ServerInfoPanel: React.FC<ServerInfoPanelProps> = ({
  title,
  description,
  rating,
  status,
  launchDate,
  playersOnline,
  maxPlayers,
  version,
  tags,
  className = '',
  socialMedia = [],
  homepageUrl,
  downloadLink,
  discordLink,
  banner,
  onDownload,
  isFavorite: initialIsFavorite = false,
  serverId,
  canEdit,
  onEdit
}) => {
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);

  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [initialIsFavorite]);

  const handleFavoriteClick = async () => {
    if (!serverId) return;
    try {
      const result = await serverAPI.toggleFavorite(serverId);
      setIsFavorite(result.favorited);
    } catch (error) {
      console.error('Failed to toggle favorite', error);
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-2xl bg-gray-100 dark:bg-gray-900 ${className}`}>
      {/* Hero Background */}
      <div className="absolute inset-0 z-0">
        {banner ? (
          <>
            <img
              src={banner}
              alt={`${title} banner`}
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-800 dark:from-gray-900 via-gray-800/80 dark:via-gray-900/80 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-600/40 dark:from-blue-900/40 to-purple-600/40 dark:to-purple-900/40" />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 p-8 md:p-10">
        <div className="flex flex-col md:flex-row justify-between items-start gap-6">

          {/* Left Side: Title & Info */}
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight drop-shadow-lg">
                {title}
              </h1>
              <ServerStatus status={status} className="text-lg px-4 py-1.5 shadow-lg" />
            </div>

            <div className="flex items-center gap-6 text-gray-300 text-sm md:text-base font-medium">
              <div className="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span>{t('server.details.launched') || 'Launched'}: {launchDate}</span>
              </div>

              {version && (
                <div className="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>{t('server.details.version') || 'Ver'}: {version}</span>
                </div>
              )}
            </div>

            {tags && tags.length > 0 && (
              <div className="pt-2">
                <ServerTags tags={tags} />
              </div>
            )}
          </div>

          {/* Right Side: Actions & Rating */}
          <div className="flex flex-col items-end gap-4 min-w-[200px]">
            {canEdit && (
              <button
                onClick={onEdit}
                className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-2 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 border border-yellow-500/20 mb-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                {t('common.edit') || 'Edit Server'}
              </button>
            )}
            <div className="bg-white/90 dark:bg-gray-800/80 backdrop-blur-md p-4 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700/50 w-full">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">{t('server.details.rating') || 'Rating'}</span>
                <Rating rating={rating} size="md" />
              </div>
              {/* Interactive Star Rating */}
              {serverId && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/50">
                  <span className="text-gray-500 dark:text-gray-400 text-xs font-medium block mb-2">
                    {t('server.rating.rate_this') || 'Rate this server'}
                  </span>
                  <StarRating serverId={serverId} size="sm" />
                </div>
              )}

            </div>

            <div className="flex gap-3 w-full">
              {homepageUrl && (
                <a
                  href={homepageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 border border-gray-300 dark:border-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  {t('server.details.website') || 'Website'}
                </a>
              )}

              {downloadLink && (
                <button
                  onClick={onDownload}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 border border-blue-400/20"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('server.details.download') || 'Download'}
                </button>
              )}
            </div>

            <div className="flex gap-3 w-full">
              {discordLink && (
                <a
                  href={discordLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 border border-[#5865F2]/20"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.419-2.1568 2.419z" />
                  </svg>
                  {t('server.details.discord') || 'Join Discord'}
                </a>
              )}

              <button
                onClick={handleFavoriteClick}
                className={`flex-1 font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 border ${isFavorite
                  ? 'bg-green-100 dark:bg-green-600/20 text-green-600 dark:text-green-400 border-green-300 dark:border-green-500/30 hover:bg-green-200 dark:hover:bg-green-600/30'
                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                  }`}
              >
                {isFavorite ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {t('server.card.in_library') || 'In Library'}
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {t('server.card.add_to_library') || 'Add to Library'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Social Media */}
        {socialMedia && socialMedia.length > 0 && (
          <div className="mt-6 flex gap-4">
            {socialMedia.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300 bg-gray-800/50 p-2 rounded-lg hover:bg-gray-700/50"
                title={link.name}
              >
                {link.icon}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerInfoPanel;